import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  packingApiService, 
  PackingHeader, 
  PackingStatus, 
  PackingHeaderCreateRequest,
  PackingHeaderUpdateRequest,
  BoxType,
  EndgoodBoxType,
  BoxTypeCreateRequest,
  EndgoodBoxTypeCreateRequest,
  CompletedWorkOrderUnpacked
} from '../services/packingApi';
import { workOrderApiService, WorkOrder } from '../services/workOrderApi';
import { SalesOrder } from '../services/salesOrderApi';
import { Endgood } from '../services/endgoodApi';
import { PACKING_WORK_AREA_ID } from '../utils/constants';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { workerApiService, Worker } from '../services/workerApi';

const PackingEntryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id, batchId } = useParams<{ id?: string; batchId?: string }>();
  const location = useLocation();
  const batchIdFromState = (location.state as { batchId?: number } | undefined)?.batchId;
  const batchIdParam = batchId ? parseInt(batchId, 10) : undefined;
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  
  // Determine if this is edit mode
  const isEditMode = Boolean(id || batchIdParam) && location.pathname.includes('/edit');
  const isViewMode = location.pathname.includes('/view');
  
  // Form data state
  const [formData, setFormData] = useState<PackingHeaderCreateRequest & { workOrderQty?: number, packingHeaderNo?: string }>({
    workOrderId: 0,
    salesOrderId: undefined,
    endgoodId: undefined,
    quantity: 0,
    rejectQuantity: 0,
    packedByWorkerId: undefined, // Worker ID
    totalBoxes: 0,
    packedOn: new Date().toISOString().split('T')[0],
    branchId: 1,
    status: PackingStatus.PACKED,
    boxTypeId: 0,

    createdBy: 1, // TODO: Get from user context
    createdOn: new Date().toISOString().slice(0, 19).replace('T', ' '),
    workOrderQty: 0,
    packingHeaderNo: ''
  });

  // Related data state
  const [workOrders, setWorkOrders] = useState<CompletedWorkOrderUnpacked[]>([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [selectedUnpackedWorkOrder, setSelectedUnpackedWorkOrder] = useState<CompletedWorkOrderUnpacked | null>(null);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrder | null>(null);
  const [selectedEndGood, setSelectedEndGood] = useState<Endgood | null>(null);
  const [originalHeaderQuantities, setOriginalHeaderQuantities] = useState<{ quantity: number; rejectQuantity: number }>({
    quantity: 0,
    rejectQuantity: 0
  });
  
  // Multiple work orders state
  interface WorkOrderPackingEntry {
    packingHeaderId?: number;
    batchId?: number;
    workOrderId: number;
    workOrderNumber: string;
    endgoodName: string;
    endgoodId: number;
    salesOrderId?: number;
    salesOrderNumber?: string;
    quantity: number;
    rejectQuantity: number;
    originalQuantity?: number;
    originalRejectQuantity?: number;
    boxTypeId: number;
    endgoodBoxTypes: EndgoodBoxType[];
    workOrderQuantity?: number; // completedQuantity from work order
    remainingToPack?: number; // remainingToPack from work order
  }
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<WorkOrderPackingEntry[]>([]);
  
  // Endgood filter state
  const [selectedEndgoodFilter, setSelectedEndgoodFilter] = useState<string | null>(null);
  // Work order search term
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState<string>('');
  // Create-mode box type (applies to all selected work orders)
  const [createEndgoodBoxTypes, setCreateEndgoodBoxTypes] = useState<EndgoodBoxType[]>([]);
  const [createBoxTypeId, setCreateBoxTypeId] = useState<number>(0);
  
  // Get unique endgoods from work orders
  const uniqueEndgoods = useMemo(() => {
    const endgoodMap = new Map<string, { key: string; name: string; endgoodId?: number }>();

    workOrders.forEach((wo) => {
      if (!wo) return;
      const name = wo.endgoodName || 'Unknown';
      const endgoodId = wo.endgoodId;
      const key = endgoodId ? `id:${endgoodId}` : `name:${name}`;

      if (!endgoodMap.has(key)) {
        endgoodMap.set(key, { key, name, endgoodId });
      }
    });

    return Array.from(endgoodMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [workOrders]);
  
  // Filter work orders based on selected endgood and search term
  const filteredWorkOrders = useMemo(() => {
    let filtered: typeof workOrders = [];

    // First filter by endgood
    if (!selectedEndgoodFilter) {
      filtered = [];
    } else if (selectedEndgoodFilter.startsWith('id:')) {
      const id = Number(selectedEndgoodFilter.slice(3));
      filtered = workOrders.filter((wo) => wo && wo.endgoodId === id);
    } else if (selectedEndgoodFilter.startsWith('name:')) {
      const name = selectedEndgoodFilter.slice(5);
      filtered = workOrders.filter((wo) => wo && (wo.endgoodName || 'Unknown') === name);
    }

    // Then filter by search term if provided
    if (workOrderSearchTerm.trim()) {
      const searchLower = workOrderSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((wo) => {
        if (!wo) return false;
        const workOrderNumber = (wo.workOrderNumber || '').toLowerCase();
        return workOrderNumber.includes(searchLower);
      });
    }

    return filtered;
  }, [workOrders, selectedEndgoodFilter, workOrderSearchTerm]);

  const [endgoodBoxTypes, setEndgoodBoxTypes] = useState<EndgoodBoxType[]>([]);
  const [availableBoxTypes, setAvailableBoxTypes] = useState<BoxType[]>([]);

  const resolveSelectedEndgoodId = useCallback((): number | null => {
    if (!selectedEndgoodFilter) return null;

    if (selectedEndgoodFilter.startsWith('id:')) {
      const id = Number(selectedEndgoodFilter.slice(3));
      return Number.isNaN(id) ? null : id;
    }

    if (selectedEndgoodFilter.startsWith('name:')) {
      const firstWO = filteredWorkOrders.find((wo) => wo && wo.endgoodId);
      return firstWO?.endgoodId ?? null;
    }

    return null;
  }, [selectedEndgoodFilter, filteredWorkOrders]);

  // 1) Always call API when End Good changes (create mode)
  useEffect(() => {
    const loadCreateEndgoodBoxTypes = async () => {
      if (isEditMode) {
        console.log('⏭️ Skipping API call - edit mode');
        return;
      }

      if (!selectedEndgoodFilter) {
        console.log('⏭️ Skipping API call - no endgood selected');
        setCreateEndgoodBoxTypes([]);
        setCreateBoxTypeId(0);
        return;
      }

      // Extract endgoodId directly
      let endgoodId: number | null = null;

      if (selectedEndgoodFilter.startsWith('id:')) {
        endgoodId = Number(selectedEndgoodFilter.slice(3));
        if (Number.isNaN(endgoodId)) {
          endgoodId = null;
        }
      } else if (selectedEndgoodFilter.startsWith('name:')) {
        // If selected by name, get endgoodId from the first filtered work order
        const firstWO = filteredWorkOrders.find((wo) => wo && wo.endgoodId);
        endgoodId = firstWO?.endgoodId ?? null;
      }

      if (!endgoodId || endgoodId === 0) {
        console.log('⏭️ Skipping API call - no valid endgoodId found', { selectedEndgoodFilter, filteredWorkOrders });
        setCreateEndgoodBoxTypes([]);
        setCreateBoxTypeId(0);
        return;
      }

      console.log('📡 Calling API for endgood box types:', endgoodId);
      try {
        // NOTE: API call happens even if availableBoxTypes isn't loaded yet
        const resp = await packingApiService.getEndgoodBoxTypesByEndgood(endgoodId.toString());
        console.log('✅ API response:', resp);
        if (resp.success && Array.isArray(resp.data) && resp.data.length > 0) {
          console.log('📦 Setting box types:', resp.data);
          setCreateEndgoodBoxTypes(resp.data);
        } else {
          console.log('⚠️ No box types in response');
          setCreateEndgoodBoxTypes([]);
          setCreateBoxTypeId(0);
        }
      } catch (e) {
        console.error('❌ Error loading endgood box types:', e);
        setCreateEndgoodBoxTypes([]);
        setCreateBoxTypeId(0);
      }
    };

    loadCreateEndgoodBoxTypes();
  }, [isEditMode, selectedEndgoodFilter, filteredWorkOrders]);

  // 2) Enrich box type names + apply default once box types and master list are ready
  useEffect(() => {
    if (isEditMode) return;
    if (!createEndgoodBoxTypes || createEndgoodBoxTypes.length === 0) return;

    const enriched = createEndgoodBoxTypes.map((ebt) => ({
      ...ebt,
      boxType: availableBoxTypes.find((bt) => bt.id === ebt.boxTypeId),
    }));

    // Avoid infinite loops by only updating when enrichment changed
    const needsUpdate = enriched.some((ebt, idx) => ebt.boxType !== createEndgoodBoxTypes[idx]?.boxType);
    if (needsUpdate) {
      setCreateEndgoodBoxTypes(enriched);
    }

    // Set default if not selected yet
    if (!createBoxTypeId || createBoxTypeId === 0) {
      const defaultBt = enriched.find((ebt) => ebt.isDefault);
      const nextBoxTypeId = defaultBt?.boxTypeId ?? enriched[0]?.boxTypeId ?? 0;
      if (nextBoxTypeId > 0) {
        setCreateBoxTypeId(nextBoxTypeId);
        setSelectedWorkOrders((prev) => prev.map((wo) => ({ ...wo, boxTypeId: nextBoxTypeId, endgoodBoxTypes: enriched })));
      }
    }
  }, [isEditMode, createEndgoodBoxTypes, availableBoxTypes, createBoxTypeId]);
  const [existingPackingEntries, setExistingPackingEntries] = useState<PackingHeader[]>([]);
  const [totalPackedQuantity, setTotalPackedQuantity] = useState(0);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [showCreateBoxTypeDialog, setShowCreateBoxTypeDialog] = useState(false);
  const [newBoxType, setNewBoxType] = useState<BoxTypeCreateRequest & { setAsDefault?: boolean }>({
    name: '',
    description: '',
    length: 0,
    width: 0,
    height: 0,
    weight: 0,
    maxQuantity: 0,
    cost: 0,
    setAsDefault: false
  });

  const [showCreateEndgoodBoxTypeDialog, setShowCreateEndgoodBoxTypeDialog] = useState(false);
  const [newEndgoodBoxType, setNewEndgoodBoxType] = useState<EndgoodBoxTypeCreateRequest>({
    endgoodId: 0,
    boxTypeId: 0,
    minQuantity: 1,
    maxQuantity: 100,
    isDefault: false,
    priority: 0
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const dataLoadedRef = useRef(false);

  // Load related data
  const loadRelatedData = useCallback(async () => {
    // console.log('🔄 loadRelatedData called at:', new Date().toISOString());
    try {
      // Use selectedBranch from header, fallback to user's defaultBranchId
      const branchId = selectedBranch || user?.defaultBranchId;
      if (!branchId) {
        toast.error('Please select a branch');
        return;
      }
      
      // console.log('📡 Making API calls with branchId:', branchId);
      const [workOrdersResponse, boxTypesResponse] = await Promise.all([
        packingApiService.getCompletedWorkOrdersUnpacked(branchId),
        packingApiService.getBoxTypes()
      ]);
      // console.log('📡 API calls completed');

      if (workOrdersResponse.success) {
        // console.log('📋 Work Orders Response:', workOrdersResponse);
        // console.log('📋 Work Orders Data:', workOrdersResponse.data);
        // console.log('📋 Work Orders Data Type:', typeof workOrdersResponse.data);
        // console.log('📋 Work Orders Data Is Array:', Array.isArray(workOrdersResponse.data));
        setWorkOrders(workOrdersResponse.data || []);
      }

      if (boxTypesResponse.success) {
        // console.log('📦 Box Types Response:', boxTypesResponse);
        // console.log('📦 Box Types Data:', boxTypesResponse.data);
        // console.log('📦 Box Types Data Type:', typeof boxTypesResponse.data);
        // console.log('📦 Box Types Data Is Array:', Array.isArray(boxTypesResponse.data));
        setAvailableBoxTypes(boxTypesResponse.data || []);
      }
    } catch (error) {
      // console.error('Error loading related data:', error);
      toast.error('Failed to load related data');
    }
  }, [selectedBranch, user?.defaultBranchId]);

  // Load existing packing entries for a work order
  const loadExistingPackingEntries = useCallback(async (workOrderId: number) => {
    try {
      const response = await packingApiService.getPackingHeadersByWorkOrder(workOrderId.toString());
      if (response.success) {
        setExistingPackingEntries(response.data || []);
        // Calculate total packed quantity
        const totalPacked = response.data?.reduce((sum, entry) => sum + entry.quantity, 0) || 0;
        setTotalPackedQuantity(totalPacked);
      }
    } catch (error) {
      // console.error('Error loading existing packing entries:', error);
      setExistingPackingEntries([]);
      setTotalPackedQuantity(0);
    }
  }, []);

  // Load packing header data for edit/view mode
  const loadPackingHeader = useCallback(async (headerId: string | undefined, batchIdOverride?: number) => {
    if (!headerId && !batchIdOverride) return;
    try {
      const branchId = selectedBranch ?? user?.defaultBranchId;
      let header: PackingHeader | undefined;
      let batchHeaders: PackingHeader[] = [];

      if (batchIdOverride) {
        const batchResponse = await packingApiService.getPackingHeadersByBatchId(batchIdOverride, branchId);
        if (batchResponse.success && Array.isArray(batchResponse.data) && batchResponse.data.length > 0) {
          batchHeaders = batchResponse.data;
          header = batchHeaders[0];
        }
      } else if (headerId) {
        const response = await packingApiService.getPackingHeaderById(headerId);
        if (response.success) {
          header = response.data;
          if (header.batchId) {
            const batchResponse = await packingApiService.getPackingHeadersByBatchId(header.batchId, branchId);
            if (batchResponse.success && Array.isArray(batchResponse.data)) {
              batchHeaders = batchResponse.data;
            }
          }
        }
      }

      if (!header) return;
      if (batchHeaders.length === 0) {
        batchHeaders = [header];
      }

      setFormData({
          workOrderId: header.workOrderId,
          salesOrderId: header.salesOrderId,
          endgoodId: header.endgoodId ?? undefined,
          quantity: header.quantity,
          rejectQuantity: header.rejectQuantity ?? 0,
          packedByWorkerId: header.packedByWorkerId || (header.packedBy ? (typeof header.packedBy === 'string' ? parseInt(header.packedBy) || undefined : header.packedBy) : undefined),
          totalBoxes: header.totalBoxes ?? 0,
          packedOn: header.packedOn 
            ? (header.packedOn.includes('T') 
                ? header.packedOn.split('T')[0] 
                : header.packedOn.split(' ')[0])
            : new Date().toISOString().split('T')[0],
          branchId: header.branchId ?? selectedBranch ?? 1,
          status: header.status ?? PackingStatus.PACKED,
          boxTypeId: header.boxTypeId,
          workOrderQty: 0, // Will be set after fetching work order
          packingHeaderNo: header.packingHeaderNo || ''
        });
        setOriginalHeaderQuantities({
          quantity: header.quantity ?? 0,
          rejectQuantity: header.rejectQuantity ?? 0
        });

        // In edit mode, load all packing headers for the batch (if available)
        if (isEditMode) {
          try {
            const batchEntries: WorkOrderPackingEntry[] = await Promise.all(
              batchHeaders.map(async (entry) => {
                let workOrderQuantity: number | undefined;
                let remainingToPack: number | undefined;

                try {
                  const unpackedSummary = await packingApiService.getUnpackedSummaryByWorkOrderId(entry.workOrderId);
                  if (unpackedSummary.success && unpackedSummary.data) {
                    workOrderQuantity = unpackedSummary.data.completedQuantity ?? workOrderQuantity;
                    remainingToPack = unpackedSummary.data.remainingToPack ?? remainingToPack;
                  }
                } catch (error) {
                  // Ignore summary errors; allow edits without remaining info
                }

                return {
                  packingHeaderId: entry.id,
                  batchId: entry.batchId ?? undefined,
                  workOrderId: entry.workOrderId,
                  workOrderNumber: entry.workOrderNumber || `WO-${entry.workOrderId}`,
                  endgoodName: entry.endgoodName || '',
                  endgoodId: entry.endgoodId || 0,
                  salesOrderId: entry.salesOrderId,
                  salesOrderNumber: entry.salesOrderNumber,
                  quantity: entry.quantity,
                  rejectQuantity: entry.rejectQuantity ?? 0,
                  originalQuantity: entry.quantity ?? 0,
                  originalRejectQuantity: entry.rejectQuantity ?? 0,
                  boxTypeId: entry.boxTypeId,
                  endgoodBoxTypes: [],
                  workOrderQuantity: workOrderQuantity,
                  remainingToPack: remainingToPack
                };
              })
            );

            setSelectedWorkOrders(batchEntries);
          } catch (error) {
            // Ignore batch load errors and keep edit mode functional
            setSelectedWorkOrders([]);
          }
        }

        // Fetch work order data separately since it might not be nested in the response
        if (header.workOrderId) {
          try {
            const workOrderResponse = await workOrderApiService.getWorkOrderById(header.workOrderId.toString());
            // console.log('🔍 Work order response:', workOrderResponse);
            if (workOrderResponse.success) {
              const workOrder = workOrderResponse.data;
              setSelectedWorkOrder(workOrder);
              setSelectedEndGood(workOrder.endgood || null);
              setSelectedSalesOrder(workOrder.salesOrder || null);
              
              // Set work order quantity and endgoodId correctly
              setFormData(prev => ({ 
                ...prev, 
                workOrderQty: workOrder.quantity || 0,
                endgoodId: workOrder.endgood?.id || workOrder.endgoodId
              }));
              
              // Fetch endgood box types for the selected work order
              if (workOrder.endgood) {
                try {
                  const boxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(workOrder.endgood.id.toString());
                  if (boxTypesResponse.success) {
                    // Manually populate the boxType relationship from availableBoxTypes
                    const enrichedBoxTypes = boxTypesResponse.data.map(ebt => ({
                      ...ebt,
                      boxType: availableBoxTypes.find(bt => bt.id === ebt.boxTypeId)
                    }));
                    setEndgoodBoxTypes(enrichedBoxTypes);
                  }
                } catch (error) {
                  // console.error('Error fetching endgood box types:', error);
                  setEndgoodBoxTypes([]);
                }
              }
            }
          } catch (error) {
            // console.error('Error fetching work order:', error);
            toast.error('Failed to load work order details');
          }
        }

        // Set sales order if available
        if (header.salesOrder) {
          setSelectedSalesOrder(header.salesOrder);
        }
    } catch (error) {
      // console.error('Error loading packing header:', error);
      toast.error('Failed to load packing header details');
    }
  }, [isEditMode, selectedBranch, user?.defaultBranchId]);

  // Load workers for packing work area on component mount
  useEffect(() => {
    const loadWorkers = async () => {
      const branchId = selectedBranch || user?.defaultBranchId;
      setLoadingWorkers(true);
      try {
        const workersData = await workerApiService.getWorkersByWorkAreaId(PACKING_WORK_AREA_ID, branchId);
        setWorkers(workersData);
        // console.log(`✅ Loaded ${workersData.length} workers for packing work area ${PACKING_WORK_AREA_ID}`);
      } catch (error) {
        // console.error('Error loading workers:', error);
        setWorkers([]);
      } finally {
        setLoadingWorkers(false);
      }
    };

    loadWorkers();
  }, [selectedBranch, user?.defaultBranchId]);

  // Load data on component mount
  useEffect(() => {
    // console.log('🚀 useEffect triggered at:', new Date().toISOString(), 'isEditMode:', isEditMode, 'id:', id);
    
    // Prevent duplicate data loading
    if (dataLoadedRef.current) {
      // console.log('🛑 Data already loaded, skipping...');
      return;
    }
    
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      
      setIsLoadingData(true);
      await loadRelatedData();
      
      if (isEditMode && isMounted) {
        await loadPackingHeader(id, batchIdParam ?? batchIdFromState);
      }
      
      if (isMounted) {
        setIsLoadingData(false);
        dataLoadedRef.current = true;
      }
    };

    loadData();

    return () => {
      // console.log('🧹 useEffect cleanup at:', new Date().toISOString());
      isMounted = false;
    };
  }, [batchIdParam, id, isEditMode, loadRelatedData, loadPackingHeader]);

  // Refresh data when branch changes (only in create mode, not edit/view mode)
  useEffect(() => {
    if (isEditMode || isViewMode) {
      // Don't refresh in edit/view mode to preserve form data
      return;
    }

    if (!selectedBranch) {
      return;
    }

    // console.log('🔄 Branch changed, refreshing work orders for branch:', selectedBranch);
    
    // Reset form data when branch changes
    setFormData(prev => ({
      ...prev,
      workOrderId: 0,
      salesOrderId: undefined,
      endgoodId: undefined,
      quantity: 0,
      totalBoxes: 0,
      boxTypeId: 0,
      packedByWorkerId: undefined,
      branchId: selectedBranch
    }));
    setSelectedWorkOrder(null);
    setSelectedSalesOrder(null);
    setSelectedEndGood(null);
    setEndgoodBoxTypes([]);
    setExistingPackingEntries([]);
    setTotalPackedQuantity(0);
    
    // Reload work orders for the new branch
    loadRelatedData();
  }, [selectedBranch, isEditMode, isViewMode, loadRelatedData]);

  // Calculate total boxes based on quantity and box type max quantity
  const calculateTotalBoxes = useCallback((quantity: number, boxTypeId: number | null) => {
    // console.log('🧮 calculateTotalBoxes called with:', { quantity, boxTypeId });
    // console.log('🧮 endgoodBoxTypes:', endgoodBoxTypes);
    
    if (!quantity || !boxTypeId) {
      // console.log('🧮 No quantity or boxTypeId, setting totalBoxes to 0');
      setFormData(prev => ({ ...prev, totalBoxes: 0 }));
      return;
    }
    
    // Find the box type to get its max quantity
    const selectedEndgoodBoxType = endgoodBoxTypes.find(ebt => ebt.boxTypeId === boxTypeId);
          // console.log('🧮 selectedEndgoodBoxType:', selectedEndgoodBoxType);
    
    if (selectedEndgoodBoxType) {
      const maxQuantityPerBox = selectedEndgoodBoxType.maxQuantity;
      const totalBoxes = Math.ceil(quantity / maxQuantityPerBox);
              // console.log('🧮 Calculation:', { quantity, maxQuantityPerBox, totalBoxes });
      setFormData(prev => ({ ...prev, totalBoxes }));
    } else {
              // console.log('🧮 No matching endgoodBoxType found for boxTypeId:', boxTypeId);
    }
  }, [endgoodBoxTypes]);

  // Debug: Log state changes
  useEffect(() => {
    // console.log('🔍 selectedSalesOrder changed:', selectedSalesOrder);
    // console.log('🔍 selectedEndGood changed:', selectedEndGood);
  }, [selectedSalesOrder, selectedEndGood]);

  // Fetch box types when selectedEndGood or selectedWorkOrder changes (fallback if not fetched during work order selection)
  useEffect(() => {
    const fetchBoxTypesForEndgood = async () => {
      // Only fetch if we have a selected work order and we don't already have box types
      if (selectedWorkOrder && endgoodBoxTypes.length === 0) {
        // Get endgoodId from work order API response (prioritize nested endgood.id)
        const endgoodId = selectedWorkOrder.endgood?.id 
          || (selectedWorkOrder.endgoodId && selectedWorkOrder.endgoodId > 0 ? selectedWorkOrder.endgoodId : null)
          || (selectedEndGood?.id && selectedEndGood.id > 0 ? selectedEndGood.id : null);
        
        if (endgoodId && endgoodId > 0) {
          // console.log('📦 useEffect: Fetching box types for endgoodId:', endgoodId);
          // console.log('📦 useEffect: Sources - selectedWorkOrder.endgood?.id:', selectedWorkOrder.endgood?.id, 'selectedWorkOrder.endgoodId:', selectedWorkOrder.endgoodId, 'selectedEndGood?.id:', selectedEndGood?.id);
          try {
            const boxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(endgoodId.toString());
            // console.log('📦 useEffect: Box types response:', boxTypesResponse);
            if (boxTypesResponse.success && boxTypesResponse.data) {
              // Manually populate the boxType relationship from availableBoxTypes
              const enrichedBoxTypes = boxTypesResponse.data.map(ebt => ({
                ...ebt,
                boxType: availableBoxTypes.find(bt => bt.id === ebt.boxTypeId)
              }));
              // console.log('📦 useEffect: Enriched box types:', enrichedBoxTypes);
              setEndgoodBoxTypes(enrichedBoxTypes);
              // Set the default box type as selected
              const defaultBoxType = enrichedBoxTypes.find(ebt => ebt.isDefault);
              if (defaultBoxType) {
                // console.log('📦 useEffect: Setting default box type:', defaultBoxType.boxTypeId);
                setFormData(prev => ({ ...prev, boxTypeId: defaultBoxType.boxTypeId }));
              }
            }
          } catch (error) {
            // console.error('❌ useEffect: Error fetching endgood box types:', error);
          }
        } else {
          // console.warn('📦 useEffect: No valid endgoodId found. selectedWorkOrder.endgood?.id:', selectedWorkOrder.endgood?.id, 'selectedWorkOrder.endgoodId:', selectedWorkOrder.endgoodId);
        }
      }
    };

    fetchBoxTypesForEndgood();
  }, [selectedEndGood?.id, selectedWorkOrder, availableBoxTypes, endgoodBoxTypes.length]);

  // Auto-calculate total boxes when endgoodBoxTypes or boxTypeId changes
  useEffect(() => {
    if (formData.boxTypeId && formData.quantity > 0 && endgoodBoxTypes.length > 0) {
      // console.log('🔄 Auto-calculating total boxes from useEffect');
      calculateTotalBoxes(formData.quantity, formData.boxTypeId);
    }
  }, [endgoodBoxTypes, formData.boxTypeId, formData.quantity, calculateTotalBoxes]);

  // Validation function to check if packing quantity exceeds available quantity
  const validatePackingQuantity = useCallback((quantity: number): { isValid: boolean; message?: string } => {
    if (!selectedUnpackedWorkOrder) {
      return { isValid: false, message: 'Please select a work order first' };
    }

    const remainingQuantity = selectedUnpackedWorkOrder?.remainingToPack ?? 0;

    if (quantity <= 0) {
      return { isValid: false, message: 'Packing quantity must be greater than 0' };
    }

    if (quantity > remainingQuantity) {
      return { 
        isValid: false, 
        message: `Cannot pack ${quantity} items. Only ${remainingQuantity} items remaining to be packed.` 
      };
    }

    return { isValid: true };
  }, [selectedUnpackedWorkOrder]);

  // Handle input changes
  const handleInputChange = useCallback((field: keyof PackingHeaderCreateRequest, value: string | number | null) => {
    // Special handling for quantity field with validation
    if (field === 'quantity' && typeof value === 'number') {
      const validation = validatePackingQuantity(value);
      if (!validation.isValid) {
        toast.error(validation.message || 'Invalid quantity');
        return; // Don't update the form data if validation fails
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate total boxes when box type changes
    if (field === 'boxTypeId') {
      const boxTypeId = typeof value === 'string' ? parseInt(value) : value;
      calculateTotalBoxes(formData.quantity, boxTypeId);
    }
  }, [formData.quantity, calculateTotalBoxes, validatePackingQuantity]);

  // Handle new box type input changes
  const handleNewBoxTypeChange = useCallback((field: keyof BoxTypeCreateRequest | 'setAsDefault', value: string | number | boolean) => {
    setNewBoxType(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle new endgood-box-type input changes
  const handleNewEndgoodBoxTypeChange = useCallback((field: keyof EndgoodBoxTypeCreateRequest, value: string | number | boolean) => {
    setNewEndgoodBoxType(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle creating a new box type
  const handleCreateBoxType = async () => {
    try {
      const response = await packingApiService.createBoxType(newBoxType);
      if (response.success) {
        toast.success('Box type created successfully!');
        
        // If set as default, create the endgood-box-type relationship
        if (newBoxType.setAsDefault && selectedEndGood && response.data) {
          try {
            const endgoodBoxTypeData = {
              endgoodId: selectedEndGood.id,
              boxTypeId: response.data.id,
              minQuantity: 1,
              maxQuantity: response.data.maxQuantity || newBoxType.maxQuantity || 1,
              isDefault: true,
              priority: 0
            };
            
            await packingApiService.createEndgoodBoxType(endgoodBoxTypeData);
            toast.success('Box type set as default for this end good!');
          } catch (relationshipError) {
            // console.error('Error creating endgood-box-type relationship:', relationshipError);
            toast.error('Box type created but failed to set as default');
          }
        }
        
        setShowCreateBoxTypeDialog(false);
        setNewBoxType({
          name: '',
          description: '',
          length: 0,
          width: 0,
          height: 0,
          weight: 0,
          maxQuantity: 0,
          cost: 0,
          setAsDefault: false
        });
        
        // Refresh both the available box types and endgood box types lists
        try {
          // Refresh available box types
          const boxTypesResponse = await packingApiService.getBoxTypes();
          if (boxTypesResponse.success) {
            setAvailableBoxTypes(boxTypesResponse.data || []);
          }
          
          // Refresh endgood box types if we have a selected end good
          if (selectedEndGood) {
            const endgoodBoxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(selectedEndGood.id.toString());
            if (endgoodBoxTypesResponse.data) {
              setEndgoodBoxTypes(endgoodBoxTypesResponse.data);
            }
          }
        } catch (refreshError) {
          // console.error('Error refreshing box types lists:', refreshError);
        }
      }
    } catch (error) {
      // console.error('Error creating box type:', error);
      toast.error('Failed to create box type');
    }
  };

  // Resolve effective endgood ID for "Create Endgood-Box Type" (from selected endgood, filter, or first selected work order)
  const getEffectiveEndgoodIdForRelationship = (): number | null => {
    if (selectedEndGood?.id) return selectedEndGood.id;
    const fromFilter = resolveSelectedEndgoodId();
    if (fromFilter != null && fromFilter > 0) return fromFilter;
    const firstWO = selectedWorkOrders[0];
    const woEndgoodId = firstWO?.endgoodId;
    if (woEndgoodId != null && woEndgoodId > 0) return woEndgoodId;
    return null;
  };

  // Handle creating a new endgood-box-type relationship
  const handleCreateEndgoodBoxType = async () => {
    try {
      const endgoodId = getEffectiveEndgoodIdForRelationship();
      if (!endgoodId) {
        toast.error('Please select an end good first (choose an endgood filter or select a work order)');
        return;
      }

      const endgoodBoxTypeData = {
        ...newEndgoodBoxType,
        endgoodId
      };

      const response = await packingApiService.createEndgoodBoxType(endgoodBoxTypeData);
      if (response.success) {
        const createdBoxTypeId = newEndgoodBoxType.boxTypeId;
        toast.success('Endgood-Box Type relationship created successfully!');
        setShowCreateEndgoodBoxTypeDialog(false);
        setNewEndgoodBoxType({
          endgoodId: 0,
          boxTypeId: 0,
          minQuantity: 1,
          maxQuantity: 100,
          isDefault: false,
          priority: 0
        });

        // Refresh both box type lists so "Selected Box Type" reflects the new relationship
        const boxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(endgoodId.toString());
        if (boxTypesResponse.success && Array.isArray(boxTypesResponse.data)) {
          setEndgoodBoxTypes(boxTypesResponse.data);
          setCreateEndgoodBoxTypes(boxTypesResponse.data);
          if (createdBoxTypeId) {
            setCreateBoxTypeId(createdBoxTypeId);
            setSelectedWorkOrders((prev) =>
              prev.map((wo) => ({ ...wo, boxTypeId: createdBoxTypeId, endgoodBoxTypes: boxTypesResponse.data }))
            );
          }
        }
      }
    } catch (error) {
      // console.error('Error creating endgood-box-type relationship:', error);
      toast.error('Failed to create endgood-box-type relationship');
    }
  };

  // Handle work order selection
  // Handle work order checkbox toggle for multi-select
  const handleWorkOrderToggle = useCallback(async (workOrder: CompletedWorkOrderUnpacked, checked: boolean) => {
    const workOrderId = (workOrder as any).workOrderId ?? (workOrder as any).id;
    if (!workOrderId) return;

    if (checked) {
      // Immediately add work order to selection with available data (non-blocking)
      const endgoodId = workOrder.endgoodId;
      const boxTypes: EndgoodBoxType[] = createEndgoodBoxTypes.length > 0 ? createEndgoodBoxTypes : [];
      const defaultBoxType = boxTypes.find((ebt) => ebt.isDefault);
      const resolvedBoxTypeId =
        (createBoxTypeId && createBoxTypeId > 0 ? createBoxTypeId : 0) ||
        defaultBoxType?.boxTypeId ||
        boxTypes[0]?.boxTypeId ||
        0;
      
      // Create initial entry with data we already have
      const initialEntry: WorkOrderPackingEntry = {
        workOrderId: workOrderId,
        workOrderNumber: workOrder.workOrderNumber || '',
        endgoodName: workOrder.endgoodName || '',
        endgoodId: endgoodId || 0,
        salesOrderId: undefined, // Will be updated async
        salesOrderNumber: undefined, // Will be updated async
        quantity: 0,
        rejectQuantity: 0,
        boxTypeId: resolvedBoxTypeId,
        endgoodBoxTypes: boxTypes,
        workOrderQuantity: workOrder.completedQuantity,
        remainingToPack: workOrder.remainingToPack
      };

      // Add immediately so user can continue selecting
      setSelectedWorkOrders(prev => [...prev, initialEntry]);

      // Fetch additional details asynchronously (non-blocking)
      (async () => {
        try {
          // Fetch full work order details
          const workOrderResponse = await workOrderApiService.getWorkOrderById(workOrderId.toString());
          if (workOrderResponse.success && workOrderResponse.data) {
            const fullWorkOrder = workOrderResponse.data;
            
            // Prefer create-mode box types (loaded once per end good). Fallback to per-workorder fetch.
            const resolvedEndgoodId = fullWorkOrder.endgood?.id || fullWorkOrder.endgoodId || endgoodId;
            let enrichedBoxTypes: EndgoodBoxType[] = createEndgoodBoxTypes;
            if ((!enrichedBoxTypes || enrichedBoxTypes.length === 0) && resolvedEndgoodId) {
              try {
                const boxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(resolvedEndgoodId.toString());
                if (boxTypesResponse.success && Array.isArray(boxTypesResponse.data)) {
                  enrichedBoxTypes = boxTypesResponse.data.map((ebt) => ({
                    ...ebt,
                    boxType: availableBoxTypes.find((bt) => bt.id === ebt.boxTypeId),
                  }));
                }
              } catch (error) {
                // ignore
              }
            }

            const defaultBoxType = enrichedBoxTypes.find((ebt) => ebt.isDefault);
            const finalBoxTypeId =
              (createBoxTypeId && createBoxTypeId > 0 ? createBoxTypeId : 0) ||
              defaultBoxType?.boxTypeId ||
              enrichedBoxTypes[0]?.boxTypeId ||
              0;
            
            // Fetch unpacked summary to get accurate remainingToPack and completedQuantity
            let workOrderQuantity = workOrder.completedQuantity;
            let remainingToPack = workOrder.remainingToPack;
            
            try {
              const unpackedSummary = await packingApiService.getUnpackedSummaryByWorkOrderId(workOrderId);
              if (unpackedSummary.success && unpackedSummary.data) {
                workOrderQuantity = unpackedSummary.data.completedQuantity ?? workOrderQuantity;
                remainingToPack = unpackedSummary.data.remainingToPack ?? remainingToPack;
              }
            } catch (error) {
              // Use values from workOrder if fetch fails
            }
            
            // Update the entry with complete data
            setSelectedWorkOrders(prev => prev.map(wo => 
              wo.workOrderId === workOrderId 
                ? {
                    ...wo,
                    workOrderId: fullWorkOrder.id,
                    workOrderNumber: workOrder.workOrderNumber || fullWorkOrder.workOrderNumber || wo.workOrderNumber,
                    endgoodName: workOrder.endgoodName || fullWorkOrder.endgoodName || wo.endgoodName,
                    endgoodId: resolvedEndgoodId || wo.endgoodId,
                    salesOrderId: fullWorkOrder.salesOrder?.id || fullWorkOrder.salesOrderId,
                    salesOrderNumber: fullWorkOrder.salesOrderNumber || undefined,
                    boxTypeId: finalBoxTypeId,
                    endgoodBoxTypes: enrichedBoxTypes,
                    workOrderQuantity: workOrderQuantity,
                    remainingToPack: remainingToPack
                  }
                : wo
            ));
          }
        } catch (error) {
          // Silently handle error - entry is already added with basic data
          console.error('Failed to load work order details:', error);
        }
      })();
    } else {
      // Remove work order from selection
      setSelectedWorkOrders(prev => prev.filter(wo => wo.workOrderId !== workOrderId));
    }
  }, [availableBoxTypes, createEndgoodBoxTypes, createBoxTypeId]);

  // Handle quantity change for a work order
  const handleWorkOrderQuantityChange = useCallback((workOrderId: number, quantity: number) => {
    setSelectedWorkOrders(prev => prev.map(wo => 
      wo.workOrderId === workOrderId 
        ? { ...wo, quantity }
        : wo
    ));
  }, []);

  // Handle reject quantity change for a work order
  const handleWorkOrderRejectQuantityChange = useCallback((workOrderId: number, rejectQuantity: number) => {
    // Ensure rejectQuantity is always a number
    const numRejectQty = typeof rejectQuantity === 'number' && !isNaN(rejectQuantity) 
      ? rejectQuantity 
      : (typeof rejectQuantity === 'string' ? (parseInt(rejectQuantity, 10) || 0) : 0);
    
    setSelectedWorkOrders(prev => prev.map(wo => 
      wo.workOrderId === workOrderId 
        ? { ...wo, rejectQuantity: numRejectQty }
        : wo
    ));
  }, []);

  // Handle box type change for a work order
  const handleWorkOrderBoxTypeChange = useCallback((workOrderId: number, boxTypeId: number) => {
    setSelectedWorkOrders(prev => prev.map(wo => 
      wo.workOrderId === workOrderId 
        ? { ...wo, boxTypeId }
        : wo
    ));
  }, []);

  const handleWorkOrderChange = useCallback(async (workOrderId: string) => {
    if (workOrderId === 'select') {
      setFormData(prev => ({ 
        ...prev, 
        workOrderId: 0,
        salesOrderId: undefined,
        endgoodId: undefined,
        quantity: 0,
        packedByWorkerId: undefined
      }));
      setSelectedWorkOrder(null);
      setSelectedUnpackedWorkOrder(null);
      setSelectedSalesOrder(null);
      setSelectedEndGood(null);
      setEndgoodBoxTypes([]);
      setExistingPackingEntries([]);
      setTotalPackedQuantity(0);
    } else {
      // Find the simplified work order from the list
      // Support both old format (id) and new format (workOrderId)
      const simplifiedWorkOrder = workOrders.find(wo => {
        if (!wo) return false;
        const woId = (wo as any).workOrderId ?? (wo as any).id;
        return woId && woId.toString() === workOrderId;
      });
      
      if (simplifiedWorkOrder) {
        // Support both old format (id) and new format (workOrderId)
        const woId = (simplifiedWorkOrder as any).workOrderId ?? (simplifiedWorkOrder as any).id;
        if (!woId) {
          toast.error('Invalid work order ID');
          return;
        }

        // Fetch unpacked summary for this work order to get accurate remainingToPack
        try {
          const unpackedSummaryResponse = await packingApiService.getUnpackedSummaryByWorkOrderId(woId);
          if (unpackedSummaryResponse.success && unpackedSummaryResponse.data) {
            // Store the unpacked work order with complete details including remainingToPack
            const unpackedSummary = unpackedSummaryResponse.data;
            setSelectedUnpackedWorkOrder(unpackedSummary);
            // Update total packed quantity from the unpacked summary
            if (unpackedSummary.totalPackedQuantity !== undefined) {
              setTotalPackedQuantity(unpackedSummary.totalPackedQuantity);
            }
            // console.log('✅ Unpacked summary loaded:', unpackedSummary);
          } else {
            // Fallback to simplified work order if summary fetch fails
            setSelectedUnpackedWorkOrder(simplifiedWorkOrder);
            // console.warn('⚠️ Failed to fetch unpacked summary, using simplified work order');
            // Still load existing packing entries to calculate totalPackedQuantity
            await loadExistingPackingEntries(woId);
          }
        } catch (error) {
          // console.error('Error fetching unpacked summary:', error);
          // Fallback to simplified work order
          setSelectedUnpackedWorkOrder(simplifiedWorkOrder);
          // Still load existing packing entries to calculate totalPackedQuantity
          await loadExistingPackingEntries(woId);
        }

        // First, set the endgood from the simplified work order data we already have
        if (simplifiedWorkOrder.endgoodName) {
          const endgoodFromSimplified: Endgood = {
            id: simplifiedWorkOrder.endgoodId || 0,
            endgoodName: simplifiedWorkOrder.endgoodName,
            sku: '',
            partNumber: '',
            description: '',
            drawingNumber: '',
            drawingImagePath: '',
            productionWeight: 0,
            isActive: true,
            createdBy: 0,
            updatedBy: 0,
            createdAt: '',
            updatedAt: '',
            hsn: {} as any,
            endgoodCategory: {} as any
          };
          setSelectedEndGood(endgoodFromSimplified);
          // console.log('✅ Endgood populated from simplified work order:', simplifiedWorkOrder.endgoodName);
        }
        
        // Fetch full work order details since we only have workOrderId, workOrderNumber, and endgoodName
        try {
          const workOrderResponse = await workOrderApiService.getWorkOrderById(woId.toString());
          // console.log('🔍 Work order response:', workOrderResponse);
          if (workOrderResponse.success && workOrderResponse.data) {
            const workOrder = workOrderResponse.data;
            // console.log('📦 Work order data:', workOrder);
            // console.log('📦 salesOrderNumber:', workOrder.salesOrderNumber);
            // console.log('📦 endgoodName:', workOrder.endgoodName);
            // console.log('📦 endgoodSku:', workOrder.endgoodSku);
            // console.log('📦 salesOrderId:', workOrder.salesOrderId);
            // console.log('📦 endgoodId:', workOrder.endgoodId);
            // console.log('📦 salesOrder (nested):', workOrder.salesOrder);
            // console.log('📦 endgood (nested):', workOrder.endgood);
            
            setSelectedWorkOrder(workOrder);
            
            setFormData(prev => ({ 
              ...prev, 
              workOrderId: workOrder.id,
              salesOrderId: workOrder.salesOrder?.id || workOrder.salesOrderId,
              endgoodId: workOrder.endgood?.id || workOrder.endgoodId,
              workOrderQty: workOrder.quantity || 0,
              quantity: 0, // Start with 0, let user enter the desired quantity
              boxTypeId: 0
            }));

            // Use sales order and endgood from work order response (flattened fields in summary API)
            // Create sales order object from flattened fields if nested object not available
            if (workOrder.salesOrder) {
              setSelectedSalesOrder(workOrder.salesOrder);
              // console.log('✅ Sales Order populated from work order (nested):', workOrder.salesOrder.soNumber);
            } else if (workOrder.salesOrderNumber) {
              // Create a minimal sales order object from flattened fields
              const salesOrderFromFields: SalesOrder = {
                id: workOrder.salesOrderId || 0,
                soNumber: workOrder.salesOrderNumber,
                orderDate: '',
                status: '',
                effectiveStartDate: '',
                effectiveEndDate: '',
                notes: '',
                refNo: '',
                refDate: '',
                totalAmount: 0,
                taxAmount: 0,
                grandTotal: 0,
                isActive: true,
                createdBy: 0,
                updatedBy: 0,
                createdAt: '',
                updatedAt: '',
                customerId: 0,
                customerName: workOrder.customerName || '',
                customerCode: '',
                branchId: workOrder.branchId || 0,
                branchName: '',
                branchCode: ''
              };
              // console.log('📝 Creating sales order from fields:', salesOrderFromFields);
              setSelectedSalesOrder(salesOrderFromFields);
              // console.log('✅ Sales Order populated from work order (flattened):', workOrder.salesOrderNumber);
            } else {
              // console.warn('⚠️ No sales order in work order response. Available fields:', Object.keys(workOrder));
            }

            // Create endgood object from flattened fields if nested object not available
            // Update with full details if available, otherwise keep the simplified one
            if (workOrder.endgood) {
              setSelectedEndGood(workOrder.endgood);
              // console.log('✅ Endgood populated from work order (nested):', workOrder.endgood.endgoodName);
            } else if (workOrder.endgoodName) {
              // Create a minimal endgood object from flattened fields
              const endgoodFromFields: Endgood = {
                id: workOrder.endgoodId || 0,
                endgoodName: workOrder.endgoodName,
                sku: workOrder.endgoodSku || '',
                partNumber: '',
                description: '',
                drawingNumber: '',
                drawingImagePath: '',
                productionWeight: 0,
                isActive: true,
                createdBy: 0,
                updatedBy: 0,
                createdAt: '',
                updatedAt: '',
                hsn: {} as any, // Placeholder - not available in flattened response
                endgoodCategory: {} as any // Placeholder - not available in flattened response
              };
              // console.log('📝 Creating endgood from fields:', endgoodFromFields);
              setSelectedEndGood(endgoodFromFields);
              // console.log('✅ Endgood populated from work order (flattened):', workOrder.endgoodName);
            } else {
              // console.warn('⚠️ No endgood in work order response. Available fields:', Object.keys(workOrder));
              // Keep the simplified endgood we set earlier
            }
            
            // Load existing packing entries for this work order (only if we don't have unpacked summary)
            // If we already have unpacked summary, it contains totalPackedQuantity, so we can skip this
            if (!selectedUnpackedWorkOrder || selectedUnpackedWorkOrder.totalPackedQuantity === undefined) {
              await loadExistingPackingEntries(workOrder.id);
            }
            
            // Fetch endgood box types using endgoodId from work order API response
            // Prioritize nested endgood.id, then workOrder.endgoodId, then selectedEndGood.id
            const endgoodId = workOrder.endgood?.id 
              || (workOrder.endgoodId && workOrder.endgoodId > 0 ? workOrder.endgoodId : null)
              || (selectedEndGood?.id && selectedEndGood.id > 0 ? selectedEndGood.id : null);
            
            // console.log('📦 Fetching box types for endgoodId:', endgoodId);
            // console.log('📦 Sources - workOrder.endgood?.id:', workOrder.endgood?.id, 'workOrder.endgoodId:', workOrder.endgoodId, 'selectedEndGood?.id:', selectedEndGood?.id);
            // console.log('📦 Full workOrder object keys:', Object.keys(workOrder));
            // console.log('📦 Full workOrder.endgood object:', workOrder.endgood);
            
            if (endgoodId && endgoodId > 0) {
              try {
                // console.log('📦 Calling getEndgoodBoxTypesByEndgood with endgoodId:', endgoodId);
                const boxTypesResponse = await packingApiService.getEndgoodBoxTypesByEndgood(endgoodId.toString());
                // console.log('📦 Box types response:', boxTypesResponse);
                if (boxTypesResponse.success && boxTypesResponse.data) {
                  // Manually populate the boxType relationship from availableBoxTypes
                  const enrichedBoxTypes = boxTypesResponse.data.map(ebt => ({
                    ...ebt,
                    boxType: availableBoxTypes.find(bt => bt.id === ebt.boxTypeId)
                  }));
                  // console.log('📦 Enriched box types:', enrichedBoxTypes);
                  setEndgoodBoxTypes(enrichedBoxTypes);
                  // Set the default box type as selected
                  const defaultBoxType = enrichedBoxTypes.find(ebt => ebt.isDefault);
                  if (defaultBoxType) {
                    // console.log('📦 Setting default box type:', defaultBoxType.boxTypeId);
                    setFormData(prev => ({ ...prev, boxTypeId: defaultBoxType.boxTypeId }));
                  } else {
                    // console.log('📦 No default box type found');
                  }
                } else {
                  // console.warn('📦 Box types response was not successful or had no data');
                  setEndgoodBoxTypes([]);
                }
              } catch (boxTypeError) {
                // console.error('❌ Error fetching endgood box types:', boxTypeError);
                setEndgoodBoxTypes([]);
              }
            } else {
              // console.warn('⚠️ No valid endgoodId found to fetch box types. endgoodId:', endgoodId);
              setEndgoodBoxTypes([]);
            }
          } else {
            toast.error('Failed to fetch work order details');
            setSelectedWorkOrder(null);
          }
        } catch (error) {
          // console.error('Error fetching work order details:', error);
          toast.error('Failed to fetch work order details');
          setSelectedWorkOrder(null);
        }
      } else {
        setSelectedWorkOrder(null);
      }
    }
  }, [workOrders, loadExistingPackingEntries, availableBoxTypes]);

  // Handle sales order selection (disabled - auto-populated from work order)
  const handleSalesOrderChange = (salesOrderId: string) => {
    // Sales order is auto-populated from work order, so this function is not needed
    // but kept for compatibility
  };

  // Validate form
  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.workOrderId) {
      errors.push('Work Order is required');
    }

    if (!formData.quantity || formData.quantity <= 0) {
      errors.push('Packing quantity must be greater than 0');
    }

    // Validate packing quantity + reject quantity against remaining quantity
    if (selectedUnpackedWorkOrder) {
      const remainingQuantity = selectedUnpackedWorkOrder?.remainingToPack ?? 0;
      const allowance = isEditMode
        ? remainingQuantity + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)
        : remainingQuantity;
      const totalQuantity = (formData.quantity || 0) + (formData.rejectQuantity || 0);
      if (totalQuantity > allowance) {
        errors.push(`Packing quantity (${formData.quantity || 0}) + Reject quantity (${formData.rejectQuantity || 0}) = ${totalQuantity} cannot exceed remaining to pack (${allowance}).`);
      }
    }

    if (!formData.branchId) {
      errors.push('Branch is required');
    }

    if (!formData.packedOn) {
      errors.push('Packed On date is required');
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // In create mode with multiple work orders, validate the table entries
    if (!isEditMode) {
      const errors: string[] = [];
      
      // Validate endgood selection
      if (!selectedEndgoodFilter) {
        errors.push('Please select an end good first');
      }
      
      // Validate work order selection
      if (selectedWorkOrders.length === 0) {
        errors.push('Please select at least one work order');
      }
      
      if (!formData.packedOn) {
        errors.push('Packed On date is required');
      }
      
      if (!formData.packedByWorkerId) {
        errors.push('Packed By worker is required');
      }
      
      if (!formData.branchId) {
        errors.push('Branch is required');
      }
      
      // Validate global box type (applies to all selected work orders)
      if (!createBoxTypeId || createBoxTypeId === 0) {
        errors.push('Please select a box type');
      }

      // Validate each work order entry
      selectedWorkOrders.forEach((woEntry) => {
        if (woEntry.quantity <= 0) {
          errors.push(`Packing quantity is required for work order ${woEntry.workOrderNumber}`);
        }
        
        // Validate packing + reject quantity doesn't exceed remaining to pack
        if (woEntry.remainingToPack !== undefined && woEntry.remainingToPack >= 0) {
          const totalQuantity = (woEntry.quantity || 0) + (woEntry.rejectQuantity || 0);
          if (totalQuantity > woEntry.remainingToPack) {
            errors.push(`Packing quantity (${woEntry.quantity || 0}) + Reject quantity (${woEntry.rejectQuantity || 0}) = ${totalQuantity} cannot exceed remaining to pack (${woEntry.remainingToPack}) for work order ${woEntry.workOrderNumber}`);
          }
        }
      });
      
      // Block submission if there are any validation errors
      if (errors.length > 0) {
        errors.forEach(error => toast.error(error));
        return; // Stop execution - do not proceed with creating packing entries
      }
      
      // Additional safety check - ensure we have selected work orders
      if (selectedWorkOrders.length === 0) {
        toast.error('Please select at least one work order');
        return; // Stop execution
      }
      
      // Create packing headers for each work order
      setIsSaving(true);
      try {
        const createdHeaders: string[] = [];
        
        // Prepare all headers data for bulk creation
        // All headers will share the same batch ID when created via bulk API
        // Backend will generate packingHeaderNo automatically
        const bulkHeadersData: PackingHeaderCreateRequest[] = selectedWorkOrders.map((woEntry) => {
          // Ensure rejectQuantity is properly set - use the actual value from woEntry
          // If it's already a number, use it directly; otherwise convert it to a number
          const rejectQty = typeof woEntry.rejectQuantity === 'number' 
            ? woEntry.rejectQuantity 
            : (woEntry.rejectQuantity !== undefined && woEntry.rejectQuantity !== null && String(woEntry.rejectQuantity).trim() !== ''
              ? (typeof woEntry.rejectQuantity === 'string' ? parseInt(woEntry.rejectQuantity, 10) : Number(woEntry.rejectQuantity))
              : 0);
          
          // Ensure it's a valid number (not NaN), default to 0 if invalid
          const finalRejectQty = (typeof rejectQty === 'number' && !isNaN(rejectQty)) ? rejectQty : 0;
          
          return {
            workOrderId: woEntry.workOrderId,
            salesOrderId: woEntry.salesOrderId,
            endgoodId: woEntry.endgoodId,
            quantity: woEntry.quantity,
            rejectQuantity: finalRejectQty,
            packedByWorkerId: formData.packedByWorkerId,
            // Don't pass totalBoxes - let backend calculate based on quantity and boxTypeId
            // This allows backend to optimize boxes at batch level for all headers in the batch
            packedOn: formData.packedOn,
            branchId: formData.branchId,
            status: formData.status,
            boxTypeId: createBoxTypeId,
            createdBy: formData.createdBy,
            createdOn: formData.createdOn
          };
        });
        
        // Create all headers in bulk - they will share the same batch ID
        // This allows the backend to intelligently create boxes that minimize NOT_FULL boxes
        const bulkResponse = await packingApiService.createBulkPackingHeaders(bulkHeadersData);
        if (bulkResponse.success && bulkResponse.data) {
          bulkResponse.data.forEach((header, index) => {
            createdHeaders.push(selectedWorkOrders[index].workOrderNumber);
          });
        }
        
        toast.success(`Successfully created ${createdHeaders.length} packing header(s)!`);
        navigate('/packing-entries');
      } catch (error) {
        toast.error('Failed to create packing headers');
      } finally {
        setIsSaving(false);
      }
      return;
    }
    
    // Edit mode with batch entries (same as create table)
    if (isEditMode && selectedWorkOrders.length > 0) {
      const errors: string[] = [];

      if (!formData.packedOn) {
        errors.push('Packed On date is required');
      }

      if (!formData.packedByWorkerId) {
        errors.push('Packed By worker is required');
      }

      if (!formData.branchId) {
        errors.push('Branch is required');
      }

      selectedWorkOrders.forEach((woEntry) => {
        if (woEntry.quantity <= 0) {
          errors.push(`Packing quantity is required for work order ${woEntry.workOrderNumber}`);
        }

        if (woEntry.remainingToPack !== undefined && woEntry.remainingToPack >= 0) {
          const allowance = (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0);
          const totalQuantity = (woEntry.quantity || 0) + (woEntry.rejectQuantity || 0);
          if (totalQuantity > allowance) {
            errors.push(`Packing quantity (${woEntry.quantity || 0}) + Reject quantity (${woEntry.rejectQuantity || 0}) = ${totalQuantity} cannot exceed remaining to pack (${allowance}) for work order ${woEntry.workOrderNumber}`);
          }
        }
      });

      if (errors.length > 0) {
        errors.forEach(error => toast.error(error));
        return;
      }

      setIsSaving(true);
      try {
        const updateRequests = selectedWorkOrders
          .filter((woEntry) => woEntry.packingHeaderId)
          .map((woEntry) => {
            const updateData: PackingHeaderUpdateRequest = {
              id: woEntry.packingHeaderId as number,
              workOrderId: woEntry.workOrderId,
              salesOrderId: woEntry.salesOrderId ?? formData.salesOrderId,
              endgoodId: woEntry.endgoodId || formData.endgoodId,
              quantity: woEntry.quantity,
              rejectQuantity: woEntry.rejectQuantity ?? 0,
              packedByWorkerId: formData.packedByWorkerId,
              packedOn: formData.packedOn,
              branchId: formData.branchId,
              status: formData.status,
              boxTypeId: woEntry.boxTypeId || formData.boxTypeId || 0
            };
            return packingApiService.updatePackingHeader(String(woEntry.packingHeaderId), updateData);
          });

        await Promise.all(updateRequests);
        toast.success('Packing entries updated successfully!');
        navigate('/packing-entries');
      } catch (error) {
        toast.error('Failed to update packing entries');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Original single work order flow (fallback)
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return; // Block submission - do not proceed with saving
    }

    setIsSaving(true);
    try {
      // Ensure salesOrderId is included from selectedSalesOrder if available
      // Check both selectedSalesOrder.id and formData.salesOrderId
      const salesOrderId = selectedSalesOrder?.id 
        || (formData.salesOrderId && formData.salesOrderId > 0 ? formData.salesOrderId : undefined)
        || (selectedWorkOrder?.salesOrder?.id)
        || (selectedWorkOrder?.salesOrderId && selectedWorkOrder.salesOrderId > 0 ? selectedWorkOrder.salesOrderId : undefined)
        || undefined;
      
      console.log('🔍 Sales Order ID sources:', {
        selectedSalesOrderId: selectedSalesOrder?.id,
        formDataSalesOrderId: formData.salesOrderId,
        workOrderSalesOrderId: selectedWorkOrder?.salesOrder?.id,
        workOrderSalesOrderIdFlat: selectedWorkOrder?.salesOrderId,
        finalSalesOrderId: salesOrderId
      });
      
      if (isEditMode && id) {
        const updateData: PackingHeaderUpdateRequest = {
          id: parseInt(id),
          ...formData,
          salesOrderId: salesOrderId,
          rejectQuantity: formData.rejectQuantity ?? 0
        };
        // console.log('📤 Updating packing header with data:', updateData);
        // console.log('📤 Update data includes salesOrderId:', updateData.salesOrderId);
        await packingApiService.updatePackingHeader(id, updateData);
      } else {
        // Build createData explicitly to ensure salesOrderId is included
        // Backend will generate packingHeaderNo automatically
        const createData: PackingHeaderCreateRequest = {
          workOrderId: formData.workOrderId,
          salesOrderId: salesOrderId, // Explicitly set, even if undefined
          endgoodId: formData.endgoodId,
          quantity: formData.quantity,
          rejectQuantity: formData.rejectQuantity ?? 0,
          packedByWorkerId: formData.packedByWorkerId,
          totalBoxes: formData.totalBoxes,
          packedOn: formData.packedOn,
          branchId: formData.branchId,
          status: formData.status,
          boxTypeId: formData.boxTypeId,
          createdBy: formData.createdBy,
          createdOn: formData.createdOn
        };
        // console.log('📤 Creating packing header with data:', createData);
        // console.log('📤 Create data includes salesOrderId:', createData.salesOrderId);
        // console.log('📤 Full createData object:', JSON.stringify(createData, null, 2));
        // console.log('📤 salesOrderId type:', typeof createData.salesOrderId);
        // console.log('📤 salesOrderId value:', createData.salesOrderId);
        await packingApiService.createPackingHeader(createData);
      }

      toast.success(`Packing header ${isEditMode ? 'updated' : 'created'} successfully!`);
      navigate('/packing-entries');
    } catch (error) {
      // console.error('Error saving packing header:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} packing header`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate('/packing-entries');
  }, [navigate]);

  // Get status icon
  const getStatusIcon = (status: PackingStatus) => {
    switch (status) {
      case PackingStatus.PACKED:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case PackingStatus.INVOICED:
        return <FileText className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  if (isLoadingData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isViewMode ? 'View' : isEditMode ? 'Edit' : 'Create'} Packing Entry
            </h1>
            <p className="text-gray-600 mt-1">
              {isViewMode ? 'View packing entry details' : isEditMode ? 'Edit packing entry information' : 'Create a new packing entry'}
            </p>
          </div>
        </div>
        
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditMode ? 'Update' : 'Create'} Packing Entry
              </>
            )}
          </Button>
        )}
      </div>

      {/* Form Sections */}
      <div className="space-y-6">
        {/* Basic Information Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200/50">
            <CardTitle className="flex items-center text-primary-700">
              <Package className="h-5 w-5 mr-2" />
              Basic Information
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Enter the basic details for the packing entry
            </p>
          </CardHeader>
          <CardContent className="p-6">
            <div className={`grid grid-cols-1 md:grid-cols-${isEditMode ? '3' : '2'} gap-4`}>
              {/* Packing Header Number - Show in edit mode */}
              {isEditMode && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Packing Header Number</Label>
                  <Input
                    value={formData.packingHeaderNo || 'N/A'}
                    disabled={true}
                    className="mt-1 bg-gray-50"
                  />
                </div>
              )}

              {/* Endgood Selection - Only in create mode */}
              {!isEditMode && (
                <div className="md:col-span-4">
                  <Label className="text-sm font-medium text-gray-700">Select End Good *</Label>
                  <Select
                    value={selectedEndgoodFilter ?? ''}
                    onValueChange={(value) => {
                      setSelectedEndgoodFilter(value || null);
                      // Clear selected work orders and search term when endgood changes
                      setSelectedWorkOrders([]);
                      setWorkOrderSearchTerm('');
                    }}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className="mt-1 border border-gray-300">
                      <SelectValue placeholder="Select an end good" />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search end goods..." debounceMs={300}>
                      {uniqueEndgoods.map((endgood) => (
                        <SelectItem key={endgood.key} value={endgood.key}>
                          {endgood.name}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                  {uniqueEndgoods.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No end goods available. Please ensure there are completed work orders.</p>
                  )}
                </div>
              )}

              {/* Work Order Selection - Multi-select with checkboxes (always shown in create mode, disabled until endgood is selected) */}
              {!isEditMode && (
                <div className="md:col-span-4">
                  <Label className="text-sm font-medium text-gray-700">Select Work Orders *</Label>
                  {/* Search input for work orders */}
                  <div className="mt-2 mb-2">
                    <Input
                      type="text"
                      placeholder={selectedEndgoodFilter ? "Search work orders..." : "Select end good first"}
                      value={workOrderSearchTerm}
                      onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                      disabled={!selectedEndgoodFilter || isViewMode}
                      className="w-full"
                    />
                  </div>
                  <div className={`mt-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2 ${!selectedEndgoodFilter ? 'bg-gray-50' : ''}`}>
                    {!selectedEndgoodFilter ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Please select an end good first to view work orders
                      </p>
                    ) : Array.isArray(filteredWorkOrders) && filteredWorkOrders.length > 0 ? (
                      filteredWorkOrders
                        .filter(workOrder => workOrder != null)
                        .map((workOrder) => {
                          const workOrderId = (workOrder as any).workOrderId ?? (workOrder as any).id;
                          if (!workOrderId) return null;
                          const isSelected = selectedWorkOrders.some(wo => wo.workOrderId === workOrderId);
                          return (
                            <div key={workOrderId} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`wo-${workOrderId}`}
                                checked={isSelected}
                                onCheckedChange={(checked) => handleWorkOrderToggle(workOrder, checked as boolean)}
                                disabled={isViewMode}
                              />
                              <Label 
                                htmlFor={`wo-${workOrderId}`} 
                                className="flex-1 cursor-pointer text-sm"
                              >
                                {workOrder.workOrderNumber || 'N/A'}
                                {(workOrder as CompletedWorkOrderUnpacked).remainingToPack !== undefined && (
                                  <span className="ml-2 text-amber-700 font-medium">
                                    (Remaining to pack: {(workOrder as CompletedWorkOrderUnpacked).remainingToPack})
                                  </span>
                                )}
                              </Label>
                            </div>
                          );
                        })
                        .filter(item => item != null)
                    ) : (
                      <p className="text-sm text-gray-500">
                        {workOrderSearchTerm.trim() 
                          ? 'No work orders found matching your search' 
                          : 'No work orders available for selected end good'}
                      </p>
                    )}
                  </div>
                  {selectedWorkOrders.length > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      {selectedWorkOrders.length} work order(s) selected
                    </p>
                  )}
                </div>
              )}
              
              {/* Work Order - Show in edit mode */}
              {isEditMode && (
                <div>
                  <Label htmlFor="workOrder" className="text-sm font-medium text-gray-700">Work Order *</Label>
                  <div className="mt-1 flex items-center h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                    {selectedWorkOrder?.workOrderNumber || 'N/A'}
                  </div>
                </div>
              )}

              {/* Default Box Type */}
              <div>
                <Label htmlFor="defaultBoxType" className="text-sm font-medium text-gray-700">Default Box Type</Label>
                {!isEditMode ? (
                  // Create mode: show default (read-only)
                  createEndgoodBoxTypes.length > 0 ? (
                    <>
                      <Select
                        value={createEndgoodBoxTypes.find(ebt => ebt.isDefault)?.boxTypeId?.toString() || 'select'}
                        onValueChange={(value) => {
                          // This is read-only, just showing the default
                        }}
                        disabled={true}
                      >
                        <SelectTrigger className="mt-1 border border-gray-300 overflow-hidden">
                          <SelectValue placeholder="Select box type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select">Select box type</SelectItem>
                          {createEndgoodBoxTypes.map((ebt) => (
                            <SelectItem key={ebt.id} value={ebt.boxTypeId.toString()}>
                              {ebt.boxType?.name || `Box Type ${ebt.boxTypeId}`} {ebt.isDefault ? '(Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const defaultBoxType = createEndgoodBoxTypes.find(ebt => ebt.isDefault);
                        if (defaultBoxType && defaultBoxType.maxQuantity) {
                          return (
                            <p className="text-xs text-gray-600 mt-1">
                              Max pieces per box: <span className="font-semibold">{defaultBoxType.maxQuantity}</span>
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value="No box types available"
                        disabled={true}
                        className="flex-1"
                      />
                      <Dialog open={showCreateBoxTypeDialog} onOpenChange={setShowCreateBoxTypeDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            type="button" 
                            size="sm" 
                            className="bg-primary-600 hover:bg-primary-700 text-white"
                            disabled={!selectedEndgoodFilter}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Create New Box Type</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="boxTypeName">Name *</Label>
                              <Input
                                id="boxTypeName"
                                value={newBoxType.name}
                                onChange={(e) => handleNewBoxTypeChange('name', e.target.value)}
                                placeholder="Enter box type name"
                              />
                            </div>
                            <div>
                              <Label htmlFor="boxTypeDescription">Description</Label>
                              <Textarea
                                id="boxTypeDescription"
                                value={newBoxType.description}
                                onChange={(e) => handleNewBoxTypeChange('description', e.target.value)}
                                placeholder="Enter description"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="boxTypeLength">Length (cm)</Label>
                                <Input
                                  id="boxTypeLength"
                                  type="number"
                                  value={newBoxType.length}
                                  onChange={(e) => handleNewBoxTypeChange('length', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <Label htmlFor="boxTypeWidth">Width (cm)</Label>
                                <Input
                                  id="boxTypeWidth"
                                  type="number"
                                  value={newBoxType.width}
                                  onChange={(e) => handleNewBoxTypeChange('width', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="boxTypeHeight">Height (cm)</Label>
                                <Input
                                  id="boxTypeHeight"
                                  type="number"
                                  value={newBoxType.height}
                                  onChange={(e) => handleNewBoxTypeChange('height', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <Label htmlFor="boxTypeWeight">Weight (kg)</Label>
                                <Input
                                  id="boxTypeWeight"
                                  type="number"
                                  value={newBoxType.weight}
                                  onChange={(e) => handleNewBoxTypeChange('weight', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="boxTypeMaxQuantity">Max Quantity</Label>
                                <Input
                                  id="boxTypeMaxQuantity"
                                  type="number"
                                  value={newBoxType.maxQuantity}
                                  onChange={(e) => handleNewBoxTypeChange('maxQuantity', parseInt(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <Label htmlFor="boxTypeCost">Cost</Label>
                                <Input
                                  id="boxTypeCost"
                                  type="number"
                                  value={newBoxType.cost}
                                  onChange={(e) => handleNewBoxTypeChange('cost', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id="setAsDefault"
                                checked={newBoxType.setAsDefault || false}
                                onChange={(e) => handleNewBoxTypeChange('setAsDefault', e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <Label htmlFor="setAsDefault" className="text-sm font-medium text-gray-700">
                                Set as default box type for this end good
                              </Label>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateBoxTypeDialog(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleCreateBoxType}
                                disabled={!newBoxType.name.trim()}
                                className="bg-primary-600 hover:bg-primary-700 text-white"
                              >
                                Create Box Type
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )
                ) : (
                  /* Edit mode: existing read-only default display */
                  endgoodBoxTypes.length > 0 ? (
                  <>
                    <Select
                      value={endgoodBoxTypes.find(ebt => ebt.isDefault)?.boxTypeId?.toString() || 'select'}
                      onValueChange={(value) => {
                        // This is read-only, just showing the default
                      }}
                      disabled={true}
                    >
                      <SelectTrigger className="mt-1 border border-gray-300 overflow-hidden">
                        <SelectValue placeholder="Select box type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Select box type</SelectItem>
                        {endgoodBoxTypes.map((ebt) => (
                          <SelectItem key={ebt.id} value={ebt.boxTypeId.toString()}>
                            {ebt.boxType?.name || `Box Type ${ebt.boxTypeId}`} {ebt.isDefault ? '(Default)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const defaultBoxType = endgoodBoxTypes.find(ebt => ebt.isDefault);
                      if (defaultBoxType && defaultBoxType.maxQuantity) {
                        return (
                          <p className="text-xs text-gray-600 mt-1">
                            Max pieces per box: <span className="font-semibold">{defaultBoxType.maxQuantity}</span>
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value="No box types available"
                      disabled={true}
                      className="flex-1"
                    />
                    <Dialog open={showCreateBoxTypeDialog} onOpenChange={setShowCreateBoxTypeDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          type="button" 
                          size="sm" 
                          className="bg-primary-600 hover:bg-primary-700 text-white"
                          disabled={!selectedWorkOrder}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Create New Box Type</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="boxTypeName">Name *</Label>
                            <Input
                              id="boxTypeName"
                              value={newBoxType.name}
                              onChange={(e) => handleNewBoxTypeChange('name', e.target.value)}
                              placeholder="Enter box type name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="boxTypeDescription">Description</Label>
                            <Textarea
                              id="boxTypeDescription"
                              value={newBoxType.description}
                              onChange={(e) => handleNewBoxTypeChange('description', e.target.value)}
                              placeholder="Enter description"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="boxTypeLength">Length (cm)</Label>
                              <Input
                                id="boxTypeLength"
                                type="number"
                                value={newBoxType.length}
                                onChange={(e) => handleNewBoxTypeChange('length', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <Label htmlFor="boxTypeWidth">Width (cm)</Label>
                              <Input
                                id="boxTypeWidth"
                                type="number"
                                value={newBoxType.width}
                                onChange={(e) => handleNewBoxTypeChange('width', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="boxTypeHeight">Height (cm)</Label>
                              <Input
                                id="boxTypeHeight"
                                type="number"
                                value={newBoxType.height}
                                onChange={(e) => handleNewBoxTypeChange('height', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <Label htmlFor="boxTypeWeight">Weight (kg)</Label>
                              <Input
                                id="boxTypeWeight"
                                type="number"
                                value={newBoxType.weight}
                                onChange={(e) => handleNewBoxTypeChange('weight', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="boxTypeMaxQuantity">Max Quantity</Label>
                              <Input
                                id="boxTypeMaxQuantity"
                                type="number"
                                value={newBoxType.maxQuantity}
                                onChange={(e) => handleNewBoxTypeChange('maxQuantity', parseInt(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <Label htmlFor="boxTypeCost">Cost</Label>
                              <Input
                                id="boxTypeCost"
                                type="number"
                                value={newBoxType.cost}
                                onChange={(e) => handleNewBoxTypeChange('cost', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="setAsDefault"
                              checked={newBoxType.setAsDefault || false}
                              onChange={(e) => handleNewBoxTypeChange('setAsDefault', e.target.checked)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <Label htmlFor="setAsDefault" className="text-sm font-medium text-gray-700">
                              Set as default box type for this end good
                            </Label>
                          </div>
                          <div className="flex justify-end gap-2 pt-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowCreateBoxTypeDialog(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={handleCreateBoxType}
                              disabled={!newBoxType.name.trim()}
                              className="bg-primary-600 hover:bg-primary-700 text-white"
                            >
                              Create Box Type
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                ))}
              </div>

              {/* Selected Box Type - Only in create mode */}
              {!isEditMode && (
                <div>
                  <Label htmlFor="selectedBoxType" className="text-sm font-medium text-gray-700">Selected Box Type *</Label>
                  {createEndgoodBoxTypes.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1">
                          <Select
                            value={createBoxTypeId > 0 ? createBoxTypeId.toString() : 'select'}
                            onValueChange={(value) => {
                              const next = value === 'select' ? 0 : parseInt(value);
                              setCreateBoxTypeId(next);
                              // Apply to all selected work orders
                              setSelectedWorkOrders((prev) =>
                                prev.map((wo) => ({ ...wo, boxTypeId: next, endgoodBoxTypes: createEndgoodBoxTypes }))
                              );
                            }}
                            disabled={isViewMode || !selectedEndgoodFilter}
                          >
                            <SelectTrigger className="border border-gray-300 overflow-hidden">
                              <SelectValue placeholder={selectedEndgoodFilter ? 'Select box type' : 'Select end good first'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="select">Select box type</SelectItem>
                              {createEndgoodBoxTypes.map((ebt) => (
                                <SelectItem key={ebt.id} value={ebt.boxTypeId.toString()}>
                                  {ebt.boxType?.name || `Box Type ${ebt.boxTypeId}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Allow creating a new box relationship any time */}
                        <Dialog open={showCreateEndgoodBoxTypeDialog} onOpenChange={setShowCreateEndgoodBoxTypeDialog}>
                          <DialogTrigger asChild>
                            <Button
                              type="button"
                              size="sm"
                              className="bg-primary-600 hover:bg-primary-700 text-white"
                              disabled={!selectedEndgoodFilter}
                              title="Add new box type relationship"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Create Endgood-Box Type Relationship</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label htmlFor="boxTypeSelect">Box Type *</Label>
                                <Select
                                  value={newEndgoodBoxType.boxTypeId ? newEndgoodBoxType.boxTypeId.toString() : '0'}
                                  onValueChange={(value) =>
                                    handleNewEndgoodBoxTypeChange('boxTypeId', value === '0' ? 0 : parseInt(value))
                                  }
                                >
                                  <SelectTrigger className="mt-1 border border-gray-300">
                                    <SelectValue placeholder="Select box type" />
                                  </SelectTrigger>
                                  <SelectContentWithSearch searchPlaceholder="Search box types..." debounceMs={300}>
                                    <SelectItem value="0">Select box type</SelectItem>
                                    {Array.isArray(availableBoxTypes) && availableBoxTypes.map((boxType) => (
                                      <SelectItem key={boxType.id} value={boxType.id.toString()}>
                                        {boxType.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContentWithSearch>
                                </Select>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="minQuantity">Min Quantity</Label>
                                  <Input
                                    id="minQuantity"
                                    type="number"
                                    value={newEndgoodBoxType.minQuantity}
                                    onChange={(e) => handleNewEndgoodBoxTypeChange('minQuantity', parseInt(e.target.value) || 1)}
                                    placeholder="1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="maxQuantity">Max Quantity</Label>
                                  <Input
                                    id="maxQuantity"
                                    type="number"
                                    value={newEndgoodBoxType.maxQuantity}
                                    onChange={(e) => handleNewEndgoodBoxTypeChange('maxQuantity', parseInt(e.target.value) || 100)}
                                    placeholder="100"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 pt-4">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setShowCreateEndgoodBoxTypeDialog(false)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  onClick={handleCreateEndgoodBoxType}
                                  disabled={!newEndgoodBoxType.boxTypeId}
                                  className="bg-primary-600 hover:bg-primary-700 text-white"
                                >
                                  Create Relationship
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                      {(() => {
                        const selectedBoxType = createEndgoodBoxTypes.find(ebt => ebt.boxTypeId === createBoxTypeId);
                        if (selectedBoxType && selectedBoxType.maxQuantity) {
                          return (
                            <p className="text-xs text-gray-600 mt-1">
                              Max pieces per box: <span className="font-semibold">{selectedBoxType.maxQuantity}</span>
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value="No box types available"
                        disabled={true}
                        className="flex-1"
                      />
                      <Dialog open={showCreateEndgoodBoxTypeDialog} onOpenChange={setShowCreateEndgoodBoxTypeDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            type="button" 
                            size="sm" 
                            className="bg-primary-600 hover:bg-primary-700 text-white"
                            disabled={!selectedEndgoodFilter}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Create Endgood-Box Type Relationship</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="boxTypeSelect">Box Type *</Label>
                              <Select
                                value={newEndgoodBoxType.boxTypeId ? newEndgoodBoxType.boxTypeId.toString() : '0'}
                                onValueChange={(value) => handleNewEndgoodBoxTypeChange('boxTypeId', value === '0' ? 0 : parseInt(value))}
                              >
                                <SelectTrigger className="mt-1 border border-gray-300">
                                  <SelectValue placeholder="Select box type" />
                                </SelectTrigger>
                                <SelectContentWithSearch searchPlaceholder="Search box types..." debounceMs={300}>
                                  <SelectItem value="0">Select box type</SelectItem>
                                  {Array.isArray(availableBoxTypes) && availableBoxTypes.map((boxType) => (
                                    <SelectItem key={boxType.id} value={boxType.id.toString()}>
                                      {boxType.name}
                                    </SelectItem>
                                  ))}
                                </SelectContentWithSearch>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="minQuantity">Min Quantity</Label>
                                <Input
                                  id="minQuantity"
                                  type="number"
                                  value={newEndgoodBoxType.minQuantity}
                                  onChange={(e) => handleNewEndgoodBoxTypeChange('minQuantity', parseInt(e.target.value) || 1)}
                                  placeholder="1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="maxQuantity">Max Quantity</Label>
                                <Input
                                  id="maxQuantity"
                                  type="number"
                                  value={newEndgoodBoxType.maxQuantity}
                                  onChange={(e) => handleNewEndgoodBoxTypeChange('maxQuantity', parseInt(e.target.value) || 100)}
                                  placeholder="100"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateEndgoodBoxTypeDialog(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleCreateEndgoodBoxType}
                                disabled={!newEndgoodBoxType.boxTypeId}
                                className="bg-primary-600 hover:bg-primary-700 text-white"
                              >
                                Create Relationship
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Show old fields only when no batch entries are loaded */}
            {isEditMode && selectedWorkOrders.length === 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                  {/* Work Order Quantity */}
                  <div>
                    <Label htmlFor="workOrderQty" className="text-sm font-medium text-gray-700">Work Order Quantity</Label>
                    <Input
                      id="workOrderQty"
                      type="number"
                      value={formData.workOrderQty}
                      placeholder="Auto-populated from work order"
                      disabled={true}
                      className="mt-1 bg-gray-50"
                    />
                  </div>

                  {/* Packing Quantity */}
                  <div>
                    <Label htmlFor="packingQty" className="text-sm font-medium text-gray-700">Packing Quantity *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="packingQty"
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
                        placeholder="Enter packing quantity"
                        max={selectedUnpackedWorkOrder ? Math.max(0, ((selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)) - (formData.rejectQuantity || 0)) : formData.workOrderQty}
                        className={`mt-1 flex-1 ${(() => {
                          if (!selectedUnpackedWorkOrder) return '';
                          const remainingQuantity = (selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0);
                          const totalQuantity = formData.quantity + (formData.rejectQuantity || 0);
                          return totalQuantity > remainingQuantity ? 'border-red-500' : '';
                        })()}`}
                      />
                      {selectedUnpackedWorkOrder && ((selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)) > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const remaining = (selectedUnpackedWorkOrder?.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0);
                            const rejectQty = formData.rejectQuantity || 0;
                            handleInputChange('quantity', Math.max(0, remaining - rejectQty));
                          }}
                          className="mt-1 text-xs"
                        >
                          Use Remaining ({((selectedUnpackedWorkOrder?.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)) - (formData.rejectQuantity || 0)})
                        </Button>
                      )}
                    </div>
                    {selectedUnpackedWorkOrder && (
                      <div className="mt-1 text-sm text-gray-600">
                        <span>Remaining to pack: </span>
                        <span className="font-semibold text-amber-700">
                          {(selectedUnpackedWorkOrder?.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)}
                        </span>
                        {(selectedUnpackedWorkOrder?.totalPackedQuantity ?? 0) > 0 && (
                          <span className="ml-2 text-gray-500">
                            (Already packed: {selectedUnpackedWorkOrder?.totalPackedQuantity ?? 0})
                          </span>
                        )}
                      </div>
                    )}
                    {(() => {
                      if (!selectedUnpackedWorkOrder || !formData.quantity) return null;
                      const remainingQuantity = (selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0);
                      const totalQuantity = formData.quantity + (formData.rejectQuantity || 0);
                      if (totalQuantity > remainingQuantity) {
                        return (
                          <p className="text-sm text-red-600 mt-1">
                            Packing quantity ({formData.quantity}) + Reject quantity ({formData.rejectQuantity || 0}) = {totalQuantity} cannot exceed remaining quantity ({remainingQuantity})
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Reject Quantity */}
                  <div>
                    <Label htmlFor="rejectQty" className="text-sm font-medium text-gray-700">Reject Quantity</Label>
                    <Input
                      id="rejectQty"
                      type="number"
                      value={formData.rejectQuantity || 0}
                      onChange={(e) => handleInputChange('rejectQuantity', parseInt(e.target.value) || 0)}
                      placeholder="Enter reject quantity"
                      min={0}
                      max={selectedUnpackedWorkOrder ? Math.max(0, ((selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0)) - (formData.quantity || 0)) : undefined}
                      className={`mt-1 ${(() => {
                        if (!selectedUnpackedWorkOrder) return '';
                        const remainingQuantity = (selectedUnpackedWorkOrder.remainingToPack ?? 0) + (originalHeaderQuantities.quantity || 0) + (originalHeaderQuantities.rejectQuantity || 0);
                        const totalQuantity = formData.quantity + (formData.rejectQuantity || 0);
                        return totalQuantity > remainingQuantity ? 'border-red-500' : '';
                      })()}`}
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Total Boxes */}
                  <div>
                    <Label htmlFor="totalBoxes" className="text-sm font-medium text-gray-700">Total Boxes</Label>
                    <Input
                      id="totalBoxes"
                      type="number"
                      value={formData.totalBoxes}
                      onChange={(e) => handleInputChange('totalBoxes', parseInt(e.target.value) || 0)}
                      placeholder="Auto-calculated"
                      disabled={true}
                      className="mt-1 bg-gray-50"
                    />
                  </div>

                  {/* Selected Box Type */}
                  <div>
                    <Label htmlFor="selectedBoxType" className="text-sm font-medium text-gray-700">Selected Box Type</Label>
                {endgoodBoxTypes.length > 0 ? (
                  <>
                    <Select
                      value={formData.boxTypeId?.toString() || 'select'}
                      onValueChange={(value) => handleInputChange('boxTypeId', value === 'select' ? null : parseInt(value))}
                      disabled={isViewMode}
                    >
                      <SelectTrigger className="mt-1 border border-gray-300 overflow-hidden">
                        <SelectValue placeholder="Select box type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Select box type</SelectItem>
                        {endgoodBoxTypes.map((ebt) => (
                          <SelectItem key={ebt.id} value={ebt.boxTypeId.toString()}>
                            {ebt.boxType?.name || `Box Type ${ebt.boxTypeId}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const selectedBoxType = endgoodBoxTypes.find(ebt => ebt.boxTypeId === formData.boxTypeId);
                      if (selectedBoxType && selectedBoxType.maxQuantity) {
                        return (
                          <p className="text-xs text-gray-600 mt-1">
                            Max pieces per box: <span className="font-semibold">{selectedBoxType.maxQuantity}</span>
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value="No box types available"
                      disabled={true}
                      className="flex-1"
                    />
                    <Dialog open={showCreateEndgoodBoxTypeDialog} onOpenChange={setShowCreateEndgoodBoxTypeDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          type="button" 
                          size="sm" 
                          className="bg-primary-600 hover:bg-primary-700 text-white"
                          disabled={!selectedEndGood}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Create Endgood-Box Type Relationship</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="boxTypeSelect">Box Type *</Label>
                            <Select
                              value={newEndgoodBoxType.boxTypeId ? newEndgoodBoxType.boxTypeId.toString() : '0'}
                              onValueChange={(value) => handleNewEndgoodBoxTypeChange('boxTypeId', value === '0' ? 0 : parseInt(value))}
                            >
                              <SelectTrigger className="mt-1 border border-gray-300">
                                <SelectValue placeholder="Select box type" />
                              </SelectTrigger>
                              <SelectContentWithSearch searchPlaceholder="Search box types..." debounceMs={300}>
                                <SelectItem value="0">Select box type</SelectItem>
                                {Array.isArray(availableBoxTypes) && availableBoxTypes.map((boxType) => (
                                  <SelectItem key={boxType.id} value={boxType.id.toString()}>
                                    {boxType.name}
                                  </SelectItem>
                                ))}
                              </SelectContentWithSearch>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="minQuantity">Min Quantity</Label>
                              <Input
                                id="minQuantity"
                                type="number"
                                value={newEndgoodBoxType.minQuantity}
                                onChange={(e) => handleNewEndgoodBoxTypeChange('minQuantity', parseInt(e.target.value) || 1)}
                                placeholder="1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="minQuantity">Max Quantity</Label>
                              <Input
                                id="maxQuantity"
                                type="number"
                                value={newEndgoodBoxType.maxQuantity}
                                onChange={(e) => handleNewEndgoodBoxTypeChange('maxQuantity', parseInt(e.target.value) || 100)}
                                placeholder="100"
                              />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="isDefault"
                              checked={newEndgoodBoxType.isDefault}
                              onChange={(e) => handleNewEndgoodBoxTypeChange('isDefault', e.target.checked)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <Label htmlFor="isDefault" className="text-sm font-medium text-gray-700">
                              Set as default
                            </Label>
                          </div>
                          {/* <div>
                            <Label htmlFor="priority">Priority</Label>
                            <Input
                              id="priority"
                              type="number"
                              value={newEndgoodBoxType.priority}
                              onChange={(e) => handleNewEndgoodBoxTypeChange('priority', parseInt(e.target.value) || 0)}
                              placeholder="0"
                            />
                          </div> */}
                          <div className="flex justify-end gap-2 pt-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowCreateEndgoodBoxTypeDialog(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={handleCreateEndgoodBoxType}
                              disabled={!newEndgoodBoxType.boxTypeId}
                              className="bg-primary-600 hover:bg-primary-700 text-white"
                            >
                              Create Relationship
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {/* Packed By */}
              <div>
                <Label htmlFor="packedBy" className="text-sm font-medium text-gray-700">Packed By</Label>
                {isViewMode ? (
                  <div className="mt-1 flex items-center h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                    {formData.packedByWorkerId ? (workers.find(w => w.id === formData.packedByWorkerId)?.name || `Worker ID: ${formData.packedByWorkerId}`) : 'N/A'}
                  </div>
                ) : (
                  <Select
                    value={formData.packedByWorkerId ? formData.packedByWorkerId.toString() : 'select'}
                    onValueChange={(value) => {
                      const workerId = value === 'select' ? null : parseInt(value);
                      handleInputChange('packedByWorkerId', workerId);
                    }}
                    disabled={loadingWorkers}
                  >
                    <SelectTrigger className="mt-1 border border-gray-300">
                      <SelectValue placeholder={loadingWorkers ? "Loading workers..." : "Select worker"} />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search workers...">
                      <SelectItem value="select">Select worker</SelectItem>
                      {loadingWorkers ? (
                        <SelectItem value="loading" disabled>Loading workers...</SelectItem>
                      ) : workers.length > 0 ? (
                        workers.map((worker) => (
                          <SelectItem key={worker.id} value={worker.id.toString()}>
                            {worker.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-workers" disabled>No workers available</SelectItem>
                      )}
                    </SelectContentWithSearch>
                  </Select>
                )}
              </div>

              {/* Packed On */}
              <div>
                <Label htmlFor="packedOn" className="text-sm font-medium text-gray-700">Packed On</Label>
                <Input
                  id="packedOn"
                  type="date"
                  value={formData.packedOn}
                  onChange={(e) => handleInputChange('packedOn', e.target.value)}
                  disabled={isViewMode}
                  className="mt-1"
                />
              </div>

              {/* Status - Show in edit mode */}
              {isEditMode && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Status</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className="text-sm">
                      {formData.status}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Work Orders Packing Table */}
        {selectedWorkOrders.length > 0 && (
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200/50">
              <CardTitle className="flex items-center text-primary-700">
                <Package className="h-5 w-5 mr-2" />
                Packing Quantities
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Enter packing quantities for each selected work order
              </p>
              {!isEditMode && (() => {
                const totalPackedQuantity = selectedWorkOrders.reduce((sum, wo) => sum + (wo.quantity || 0), 0);
                const totalRejectQuantity = selectedWorkOrders.reduce((sum, wo) => sum + (wo.rejectQuantity || 0), 0);
                const selectedBoxType = createEndgoodBoxTypes.find((ebt) => ebt.boxTypeId === createBoxTypeId) ||
                  (selectedWorkOrders.length > 0 ? selectedWorkOrders[0].endgoodBoxTypes.find((ebt) => ebt.boxTypeId === createBoxTypeId) : undefined);
                const maxQuantityPerBox = selectedBoxType?.maxQuantity || 1;
                // Calculate boxes only using packed quantity (not reject quantity)
                const totalBoxes = Math.ceil(totalPackedQuantity / maxQuantityPerBox);
                
                return totalPackedQuantity > 0 || totalRejectQuantity > 0 ? (
                  <div className="mt-3 p-3 bg-primary-100/30 rounded-lg border border-primary-200/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-primary-700">Total Packed Quantity:</span>
                        <span className="ml-2 text-lg font-bold text-primary-800">{totalPackedQuantity}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-primary-700">Total Reject Quantity:</span>
                        <span className="ml-2 text-lg font-bold text-primary-800">{totalRejectQuantity}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-primary-700">Total Boxes (Calculated):</span>
                        <span className="ml-2 text-lg font-bold text-primary-800">{totalBoxes}</span>
                        <span className="ml-1 text-xs text-primary-600">({maxQuantityPerBox} pieces per box)</span>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </CardHeader>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Work Order</TableHead>
                      <TableHead>Sales Order</TableHead>
                      <TableHead>Work Order Quantity</TableHead>
                      <TableHead>Remaining to Pack</TableHead>
                      <TableHead>Packing Quantity *</TableHead>
                      <TableHead>Reject Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedWorkOrders.map((woEntry) => (
                      <TableRow key={woEntry.workOrderId}>
                        <TableCell className="font-medium">{woEntry.workOrderNumber}</TableCell>
                        <TableCell>{woEntry.salesOrderNumber || 'N/A'}</TableCell>
                        <TableCell>{woEntry.workOrderQuantity ?? 'N/A'}</TableCell>
                        <TableCell>
                          {woEntry.remainingToPack !== undefined && woEntry.remainingToPack > 0 ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const allowance = isEditMode
                                  ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                  : (woEntry.remainingToPack ?? 0);
                                handleWorkOrderQuantityChange(woEntry.workOrderId, allowance);
                              }}
                              className="w-full text-xs"
                              disabled={isViewMode}
                            >
                              Use Remaining ({isEditMode
                                ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                : woEntry.remainingToPack})
                            </Button>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <Input
                              type="number"
                              value={woEntry.quantity || ''}
                              onChange={(e) => handleWorkOrderQuantityChange(woEntry.workOrderId, parseInt(e.target.value) || 0)}
                              placeholder="Enter quantity"
                              min={0}
                              max={woEntry.remainingToPack !== undefined ? Math.max(0, ((isEditMode
                                ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                : (woEntry.remainingToPack ?? 0)) - (woEntry.rejectQuantity || 0))) : undefined}
                              className={`w-32 ${(() => {
                                if (woEntry.remainingToPack === undefined) return '';
                                const allowance = isEditMode
                                  ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                  : (woEntry.remainingToPack ?? 0);
                                const totalQuantity = woEntry.quantity + (woEntry.rejectQuantity || 0);
                                return totalQuantity > allowance ? 'border-red-500' : '';
                              })()}`}
                            />
                            {(() => {
                              if (woEntry.remainingToPack === undefined) return null;
                              const allowance = isEditMode
                                ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                : (woEntry.remainingToPack ?? 0);
                              const totalQuantity = woEntry.quantity + (woEntry.rejectQuantity || 0);
                              if (totalQuantity > allowance) {
                                return (
                                  <p className="text-xs text-red-600 mt-1">
                                    Packing + Reject ({totalQuantity}) exceeds remaining ({allowance})
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={woEntry.rejectQuantity !== undefined && woEntry.rejectQuantity !== null ? woEntry.rejectQuantity : ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = value === '' ? 0 : (parseInt(value, 10) || 0);
                              handleWorkOrderRejectQuantityChange(woEntry.workOrderId, numValue);
                            }}
                            placeholder="Enter reject quantity"
                            min={0}
                            max={woEntry.remainingToPack !== undefined ? Math.max(0, ((isEditMode
                              ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                              : (woEntry.remainingToPack ?? 0)) - (woEntry.quantity || 0))) : undefined}
                            className={`w-32 ${(() => {
                              if (woEntry.remainingToPack === undefined) return '';
                              const allowance = isEditMode
                                ? (woEntry.remainingToPack ?? 0) + (woEntry.originalQuantity ?? 0) + (woEntry.originalRejectQuantity ?? 0)
                                : (woEntry.remainingToPack ?? 0);
                              const totalQuantity = woEntry.quantity + (woEntry.rejectQuantity || 0);
                              return totalQuantity > allowance ? 'border-red-500' : '';
                            })()}`}
                            disabled={isViewMode}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Packing Information Section */}
        {selectedWorkOrder && existingPackingEntries.length > 0 && (
          <Card className="border border-amber-200 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-200/50">
              <CardTitle className="flex items-center text-amber-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                Existing Packing Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Previous packing entries for this work order
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-700">
                      {selectedUnpackedWorkOrder?.completedQuantity ?? selectedWorkOrder?.quantity ?? 0}
                    </div>
                    <div className="text-sm text-amber-600">Completed Quantity</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-700">
                      {selectedUnpackedWorkOrder?.totalPackedQuantity ?? totalPackedQuantity}
                    </div>
                    <div className="text-sm text-amber-600">Already Packed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-700">
                      {selectedUnpackedWorkOrder?.remainingToPack ?? 0}
                    </div>
                    <div className="text-sm text-amber-600">Remaining to Pack</div>
                  </div>
                </div>

                {/* Existing Entries Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Packing No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Packed By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Packed On
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {existingPackingEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {entry.packingHeaderNo}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(() => {
                              const packedByWorkerId = entry.packedByWorkerId || entry.packedBy;
                              if (!packedByWorkerId) return 'N/A';
                              if (typeof packedByWorkerId === 'string') return packedByWorkerId;
                              const workerId = typeof packedByWorkerId === 'number' ? packedByWorkerId : parseInt(String(packedByWorkerId));
                              if (isNaN(workerId)) return String(packedByWorkerId);
                              return workers.find(w => w.id === workerId)?.name || `Worker ID: ${workerId}`;
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.packedOn ? new Date(entry.packedOn).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge 
                              variant={entry.status === PackingStatus.PACKED ? 'default' : 'secondary'}
                              className={
                                entry.status === PackingStatus.PACKED 
                                  ? 'bg-green-100 text-green-800' 
                                  : entry.status === PackingStatus.INVOICED
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }
                            >
                              {entry.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons at Bottom */}
        {!isViewMode && (
          <div className="flex justify-end pt-6 pb-4">
            <Button
              onClick={handleSubmit}
              disabled={isSaving}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update' : 'Create'} Packing Entry
                </>
              )}
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default PackingEntryForm;
